import React, { useEffect, useState } from 'react';
import { isJsonPayload } from '../../../shared/api/payload';
import PropTypes from 'prop-types';
import axios from 'axios';
import debounce from 'lodash/debounce';
import Flatpickr from 'react-flatpickr';
import AsyncSelect from 'react-select/async';
import { toast } from 'react-toastify';
import { Select as SetlistSelect } from '@opendate-app/setlist';
import RoomSelector from '../../../shared/RoomSelector';
import { mergeSelectStyles } from '../../../shared/darkSelectStyles';
import { offsetDateForBrowser, offsetDateForServer } from '../../../shared/timeZoneLogic';
import { applyDefaultStart, resolveTimeDefaults } from '../../../shared/eventTimeDefaults';
import '../styles/v3CreateConfirmModal.css';

const flatPickrFormat = 'n/j/y at h:iK';
const selectStyles = mergeSelectStyles({});

const fetchTemplates = debounce((team, inputValue, callback) => {
  axios
    .get(`/teams/${team.id}/event_templates`, { params: inputValue ? { q: inputValue } : {} })
    .then(({ data }) => callback((data || []).map((t) => ({ value: t.id, label: t.name }))))
    .catch(() => callback([]));
}, 250);

const V3CreateConfirmModal = ({ team, csrfToken, offer, onClose, onSuccess }) => {
  const rooms = team.rooms || [];
  const venueOwnerships = team.venue_ownerships || [];
  const venueTimeZone = offer.venue_time_zone || null;

  const venueOwnershipIdForVenue = venueOwnerships
    .filter((vo) => vo.venueable_id === offer.venue_id)
    .map((vo) => vo.id)[0];

  const initialRooms = rooms
    .filter((r) => {
      if (Array.isArray(offer.room_ids) && offer.room_ids.length) return offer.room_ids.includes(r.id);
      if (offer.venue_ownership_id) return r.venue_ownership_id === offer.venue_ownership_id;
      if (venueOwnershipIdForVenue) return r.venue_ownership_id === venueOwnershipIdForVenue;
      return false;
    })
    .map((r) => ({ value: r.id, label: r.name, room: r, venueOwnershipId: r.venue_ownership_id }));

  const [eventTemplate, setEventTemplate] = useState(null);
  const [selectedRooms, setSelectedRooms] = useState(initialRooms);
  const [startTime, setStartTime] = useState(null);
  const [doorTime, setDoorTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [doorsInterval, setDoorsInterval] = useState(null);
  const [endInterval, setEndInterval] = useState(null);
  const [clearHolds, setClearHolds] = useState(false);
  const seatMaps = offer.venue_seat_maps || [];
  const [seatMapId, setSeatMapId] = useState(seatMaps.some((chart) => chart.id === offer.seating_chart_id) ? String(offer.seating_chart_id) : '');
  const [saving, setSaving] = useState(false);

  const canSubmit = startTime && doorTime && endTime && selectedRooms.length > 0 && !saving;
  const portal = typeof document !== 'undefined' ? document.body : null;

  useEffect(() => {
    let active = true;
    axios
      .get(`/teams/${team.id}/offers/v3/${offer.id}/confirm_defaults`)
      .then(({ data }) => {
        if (!active || !data) return;
        if (data.start_time) setStartTime(offsetDateForBrowser(data.start_time, venueTimeZone));
        if (data.door_time) setDoorTime(offsetDateForBrowser(data.door_time, venueTimeZone));
        if (data.end_time) setEndTime(offsetDateForBrowser(data.end_time, venueTimeZone));
        if (data.doors_default) setDoorsInterval(data.doors_default);
        if (data.event_end_default) setEndInterval(data.event_end_default);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [team.id, offer.id]);

  // Recompute Doors/End from the team's defaults whenever Start changes, mirroring
  // the dashboard event form (applyTimeDefaults). interval_tense decides direction.
  const applyInterval = (base, interval) => {
    if (!base || !interval) return null;
    const result = new Date(base);
    const delta = (interval.interval_tense === 'before' ? -1 : 1) * interval.interval_value;
    if (interval.interval_unit === 'minutes') result.setMinutes(result.getMinutes() + delta);
    else if (interval.interval_unit === 'hours') result.setHours(result.getHours() + delta);
    return result;
  };

  const handleStartChange = (d) => {
    setStartTime(d);
    if (!d) return;
    const pinned = resolveTimeDefaults({ start_time: d }, null, null, team.event_time_defaults, true, true);
    const door = pinned.door_time || applyInterval(d, doorsInterval);
    const end = pinned.end_time || applyInterval(d, endInterval);
    if (door) setDoorTime(door);
    if (end) setEndTime(end);
  };

  const handleStartPick = (dates, _dateText, instance) => {
    if (dates.length === 0) return;
    let picked = dates[0];
    if (!startTime) {
      picked = applyDefaultStart(team.event_time_defaults, picked);
      instance.setDate(picked, false);
    }
    handleStartChange(picked);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      const { data } = await axios.post(
        `/teams/${team.id}/offers/v3/${offer.id}/confirm.json`,
        {
          confirm: {
            event_template_id: eventTemplate ? eventTemplate.value : null,
            start_time: offsetDateForServer(startTime, venueTimeZone),
            door_time: offsetDateForServer(doorTime, venueTimeZone),
            end_time: offsetDateForServer(endTime, venueTimeZone),
            new_room_ids: selectedRooms.map((r) => r.value),
          },
          clear_holds_on_day: offer.linked_to_hold ? clearHolds : false,
          seating_chart_id: seatMapId || null,
        },
        { headers: { 'X-CSRF-TOKEN': csrfToken } },
      );
      if (!isJsonPayload(data)) {
        toast.error('Could not create the confirm. Please reload the page and try again.');
        return;
      }
      toast.success('Event confirmed');
      if (data.seat_map_not_attached) toast.warning("The seat map couldn't be attached. Pick it on the show's Ticketing page.");
      if (data.chosen_offer_left_open) toast.warning("The show is confirmed, but this offer wasn't accepted: another offer for this artist has a payment recorded. Unmark that payment, then accept this offer.");
      onSuccess(data);
    } catch (err) {
      const detail = Array.isArray(err?.response?.data) ? err.response.data.join(', ') : null;
      toast.error(detail ? `Could not confirm: ${detail}` : 'Could not create the confirm.');
    } finally {
      setSaving(false);
    }
  };

  const dateField = (label, value, onClose, testId, onChange) => (
    <div className="v3-create-confirm-modal__field">
      <label className="v3-create-confirm-modal__label">{label}</label>
      <div className="v3-create-confirm-modal__date">
        <i className="far fa-calendar" aria-hidden="true" />
        <Flatpickr
          className="v3-create-confirm-modal__date-input"
          options={{ enableTime: true, dateFormat: flatPickrFormat }}
          value={value}
          onClose={(d) => onClose(d[0])}
          onChange={onChange}
          data-testid={testId}
        />
      </div>
    </div>
  );

  return (
    <div className="v3-create-confirm-modal">
      <div className="v3-create-confirm-modal__header">
        <span className="v3-create-confirm-modal__title">Create a Confirm</span>
        <button
          type="button"
          className="v3-create-confirm-modal__close"
          aria-label="Close"
          onClick={onClose}
        >
          <i className="fa fa-times" aria-hidden="true" />
        </button>
      </div>

      <form className="v3-create-confirm-modal__body" onSubmit={handleSubmit}>
        <div className="v3-create-confirm-modal__field">
          <label className="v3-create-confirm-modal__label">
            Template <span className="v3-create-confirm-modal__optional">(optional)</span>
          </label>
          <AsyncSelect
            cacheOptions
            defaultOptions
            styles={selectStyles}
            classNamePrefix="v3cc-select"
            menuPortalTarget={portal}
            value={eventTemplate}
            loadOptions={(inputValue, callback) => fetchTemplates(team, inputValue, callback)}
            onChange={setEventTemplate}
            placeholder="Select a template"
          />
        </div>

        <div className="v3-create-confirm-modal__field">
          <label className="v3-create-confirm-modal__label">Venue</label>
          <RoomSelector
            rooms={rooms}
            venueOwnerships={venueOwnerships}
            selectedRooms={selectedRooms}
            menuPortalTarget={portal}
            classNamePrefix="v3cc-select"
            onChange={(selected) => setSelectedRooms(selected || [])}
          />
        </div>

        {seatMaps.length > 0 && (
          <div className="v3-create-confirm-modal__field">
            <SetlistSelect
              label="Seat map (optional)"
              aria-label="Seat map"
              options={[{ value: '', label: 'No seat map' }, ...seatMaps.map((chart) => ({ value: String(chart.id), label: chart.name }))]}
              value={seatMapId}
              onChange={(value) => setSeatMapId(value || '')}
            />
          </div>
        )}

        {dateField('Event Start Date/Time', startTime, handleStartChange, 'confirm-start', handleStartPick)}
        {dateField('Doors Date/Time', doorTime, (d) => setDoorTime(d), 'confirm-doors')}
        {dateField('Event End Date/Time', endTime, (d) => setEndTime(d), 'confirm-end')}

        {offer.linked_to_hold && (
          <label className="v3-create-confirm-modal__checkbox">
            <input
              type="checkbox"
              data-testid="confirm-clear-holds"
              checked={clearHolds}
              onChange={(e) => setClearHolds(e.target.checked)}
            />
            Clear all relevant holds on this day
          </label>
        )}

        <button
          type="submit"
          className="v3-create-confirm-modal__submit"
          disabled={!canSubmit}
          data-testid="confirm-submit"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </form>
    </div>
  );
};

V3CreateConfirmModal.propTypes = {
  team: PropTypes.object.isRequired,
  csrfToken: PropTypes.string.isRequired,
  offer: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default V3CreateConfirmModal;
